import { useState } from "react";
import {
  MapPin,
  LayoutGrid,
  FileText,
  Shield,
  Navigation,
  Eye,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";

/**
 * EASA-Referenz für das Web. Spiegelt den mobilen Screen
 * `client/screens/EasaReferenceScreen.tsx` 1:1. Da der dortige Datenmodul-Import
 * (`@client/lib/categoryAdvice`) transitiv RN-Code (`@/lib/storage` → AsyncStorage)
 * zieht, sind die rein statischen EASA-Inhalte hier identisch eingebettet.
 */

interface GeoZoneLegendItem {
  color: "red" | "yellow" | "green" | "blue";
  englishTerm: string;
  title: string;
  description: string;
}

const EASA_GEO_ZONE_LEGEND: GeoZoneLegendItem[] = [
  {
    color: "red",
    englishTerm: "Excluded geo-zone",
    title: "Rot — Ausgeschlossen",
    description:
      "Flüge sind für alle oder bestimmte Klassen verboten — der Drohnenbetrieb ist hier nicht erlaubt.",
  },
  {
    color: "yellow",
    englishTerm: "Restricted geo-zone",
    title: "Gelb — Eingeschränkt",
    description:
      "Der Betrieb ist eingeschränkt und an Bedingungen geknüpft. Eine vorherige Genehmigung ist erforderlich, z. B. in der Nähe von Flughäfen, Hubschrauberlandeplätzen, Nationalparks, Militäranlagen, Krankenhäusern, Kernkraftwerken oder Industrieanlagen.",
  },
  {
    color: "green",
    englishTerm: "Facilitated geo-zone",
    title: "Grün — Erleichtert",
    description:
      "Der Betrieb in der offenen Kategorie wird erleichtert — du darfst hier fliegen.",
  },
  {
    color: "blue",
    englishTerm: "U-Space airspace",
    title: "Blau — U-Space",
    description:
      "Ein Teil des unteren Luftraums, in dem der Betrieb für Drohnen und andere Fahrzeuge digital verwaltet wird.",
  },
];

interface OpenCategoryRow {
  classLabel: string;
  subcategory: string;
  operationalLimits: string;
  operatorRegistration: string;
  pilotCompetency: string;
  minAge: string;
}

const EASA_OPEN_CATEGORY_TABLE: OpenCategoryRow[] = [
  {
    classLabel: "Privat gebaut / Altbestand < 250 g",
    subcategory: "A1",
    operationalLimits:
      "Überflug unbeteiligter Personen mit äußerster Vorsicht erlaubt, nicht über Menschenansammlungen. Max. 120 m AGL, nur VLOS.",
    operatorRegistration:
      "Nur erforderlich, wenn ein Sensor zur Erfassung personenbezogener Daten (z. B. Kamera) verbaut ist und die Drohne kein Spielzeug ist.",
    pilotCompetency: "Kein Kompetenznachweis nötig — nur Sicherheitsregeln kennen.",
    minAge: "Kein Mindestalter.",
  },
  {
    classLabel: "C0 (< 250 g)",
    subcategory: "A1",
    operationalLimits:
      "Überflug unbeteiligter Personen erlaubt (vermeiden), nicht über Menschenansammlungen. Max. 120 m AGL, max. 19 m/s, nur VLOS.",
    operatorRegistration:
      "Nur erforderlich, wenn Kamera/Sensor verbaut ist und die Drohne kein Spielzeug ist.",
    pilotCompetency: "Kein formaler Nachweis — Herstellerhinweise lesen, Sicherheitsregeln kennen.",
    minAge: "16 Jahre (Mitgliedstaaten können auf 12 absenken).",
  },
  {
    classLabel: "C1 (< 900 g)",
    subcategory: "A1",
    operationalLimits:
      "Kein gezielter Überflug unbeteiligter Personen, nicht über Menschenansammlungen. Max. 120 m AGL, max. 19 m/s, nur VLOS. Remote-ID + Geo-Sensibilisierung aktiv.",
    operatorRegistration: "Erforderlich.",
    pilotCompetency: "EU-Kompetenznachweis A1/A3 (Online-Lehrgang + Online-Prüfung).",
    minAge: "16 Jahre (Mitgliedstaaten können auf 12 absenken).",
  },
  {
    classLabel: "C2 (< 4 kg)",
    subcategory: "A2 (bzw. A3)",
    operationalLimits:
      "A2: Mindestabstand 30 m zu Personen (5 m im Langsamflugmodus ≤ 3 m/s). Ohne A2-Zeugnis nur A3 (150 m Abstand). Max. 120 m AGL, nur VLOS. Remote-ID aktiv.",
    operatorRegistration: "Erforderlich.",
    pilotCompetency:
      "A2: EU-Kompetenznachweis A1/A3 + praktische Selbstschulung + EU-Fernpilotenzeugnis A2 (Theorieprüfung). A3: EU-Kompetenznachweis A1/A3.",
    minAge: "16 Jahre (Mitgliedstaaten können auf 12 absenken).",
  },
  {
    classLabel: "C3 (< 25 kg)",
    subcategory: "A3",
    operationalLimits:
      "150 m Abstand zu Wohn-, Gewerbe-, Industrie- und Erholungsgebieten, fernab von Personen. Max. 120 m AGL, nur VLOS. Remote-ID + Geo-Sensibilisierung aktiv.",
    operatorRegistration: "Erforderlich.",
    pilotCompetency: "EU-Kompetenznachweis A1/A3 (Online-Lehrgang + Online-Prüfung).",
    minAge: "16 Jahre (Mitgliedstaaten können auf 12 absenken).",
  },
  {
    classLabel: "C4 (< 25 kg)",
    subcategory: "A3",
    operationalLimits:
      "Wie C3, aber ohne automatische Steuerungssysteme. Keine Remote-ID, keine Geo-Sensibilisierung. 150 m Abstand zu besiedelten Gebieten. Max. 120 m AGL, nur VLOS.",
    operatorRegistration: "Erforderlich.",
    pilotCompetency: "EU-Kompetenznachweis A1/A3 (Online-Lehrgang + Online-Prüfung).",
    minAge: "16 Jahre (Mitgliedstaaten können auf 12 absenken).",
  },
];

interface SpecificRoute {
  key: string;
  title: string;
  description: string;
}

const EASA_SPECIFIC_CATEGORY = {
  intro:
    "Die spezielle Kategorie gilt, wenn der Betrieb außerhalb der Grenzen der offenen Kategorie stattfindet. Vor dem Flug ist in der Regel eine Betriebsgenehmigung der nationalen Luftfahrtbehörde (in DE: Landesluftfahrtbehörde bzw. LBA) erforderlich.",
  whenApplies: [
    "BVLOS — Betrieb außerhalb der Sichtweite",
    "Drohne mit MTOM über 25 kg",
    "Flug höher als 120 m über Grund",
    "Abwurf von Gegenständen/Material",
    "Betrieb im urbanen Umfeld mit MTOM über 4 kg oder ohne Klassenkennzeichen",
  ],
  routes: [
    {
      key: "STS",
      title: "STS — Standardszenario",
      description:
        "Für vordefinierte Standardszenarien (STS-01, STS-02) genügt eine Betreibererklärung an die Behörde statt einer Einzelgenehmigung. Voraussetzung sind passende C5-/C6-Drohnen und die erforderliche Pilotenkompetenz.",
    },
    {
      key: "PDRA",
      title: "PDRA — Predefined Risk Assessment",
      description:
        "Vordefinierte Risikobewertung der Behörde für häufige Einsatzarten. Der Betreiber bezieht sich auf eine PDRA und muss die SORA nicht vollständig selbst durchführen — vereinfacht den Genehmigungsantrag.",
    },
    {
      key: "SORA",
      title: "Genehmigung via SORA",
      description:
        "Specific Operations Risk Assessment: vollständige eigene Risikobewertung des geplanten Betriebs. Erforderlich, wenn kein STS und keine PDRA passt. Ergebnis ist ein Antrag auf Betriebsgenehmigung.",
    },
    {
      key: "LUC",
      title: "LUC — Light UAS Operator Certificate",
      description:
        "Zeugnis für Betreiber von Leicht-UAS. Wird der LUC erteilt, darf der Betreiber bestimmte Betriebsarten selbst genehmigen, ohne für jeden Flug eine Einzelgenehmigung zu beantragen.",
    },
  ] as SpecificRoute[],
  operationsManual:
    "Für den Betrieb in der speziellen Kategorie ist ein Betriebshandbuch (Operations Manual) erforderlich, das Verfahren, Verantwortlichkeiten und Sicherheitsmaßnahmen festlegt.",
  registration:
    "Wie in der offenen Kategorie muss sich der Betreiber (nicht die einzelne Drohne) registrieren — im Mitgliedstaat des Wohnsitzes bzw. Hauptgeschäftssitzes. Eine bestehende Registrierung gilt auch für die spezielle Kategorie.",
};

interface EasaTopicSection {
  title: string;
  paragraphs: string[];
  link?: { label: string; url: string };
}

const EASA_INSURANCE: EasaTopicSection = {
  title: "Versicherung",
  paragraphs: [
    "Eine Haftpflichtversicherung ist für Drohnen ab 20 kg EU-weit vorgeschrieben. Viele Mitgliedstaaten — darunter Deutschland — verlangen jedoch unabhängig vom Gewicht eine Versicherung für jeden Drohnenbetrieb.",
    "Empfehlung: Vor jedem Flug prüfen, ob für das jeweilige Land und die Drohne eine Versicherungspflicht besteht, und einen ausreichenden Versicherungsschutz nachweisen können.",
  ],
  link: { label: "EASA — Drohnen-Versicherung", url: "https://www.easa.europa.eu/light/topics/drone-insurance" },
};

const EASA_TRAVELLING: EasaTopicSection = {
  title: "Reisen mit Drohnen",
  paragraphs: [
    "Die EU-Drohnenregeln gelten in allen EASA-Mitgliedstaaten. Betreiberregistrierung und Kompetenznachweise, die in einem Mitgliedstaat ausgestellt wurden, werden in den anderen anerkannt — du registrierst dich nur in deinem Wohnsitzland.",
    "Geo-Zonen und nationale Zusatzregeln (z. B. Mindestalter, Versicherung) unterscheiden sich je Land. Vor einer Reise immer die Geo-Zonen und Bestimmungen des Ziellandes über dessen nationale Luftfahrtbehörde prüfen.",
  ],
  link: { label: "EASA — Reisen mit Drohnen", url: "https://www.easa.europa.eu/light/topics/travelling-drones" },
};

const EASA_FPV: EasaTopicSection = {
  title: "FPV / Drohnenrennen (Brille)",
  paragraphs: [
    "Beim Flug mit FPV-Brille (First Person View) sieht der Fernpilot nicht mehr direkt — der Betrieb gilt als BVLOS. In der offenen Kategorie ist FPV daher nur zulässig, wenn ein Luftraumbeobachter (Spotter) direkt neben dem Piloten steht, ununterbrochen Sichtkontakt zur Drohne hält und den Piloten warnen kann.",
    "Drohnenrennen und FPV-Veranstaltungen können im Rahmen anerkannter Luftsportvereine/-verbände nach gesonderten Regeln stattfinden. Ohne Spotter oder außerhalb dieser Rahmen ist FPV der speziellen Kategorie zuzuordnen.",
  ],
  link: {
    label: "EASA — Drohnenrennen & FPV-Brille",
    url: "https://www.easa.europa.eu/light/topics/drone-racing-and-flying-drone-goggles-first-person-view-fpv",
  },
};

const ZONE_COLORS: Record<string, string> = {
  red: "#C53030",
  yellow: "#D97706",
  green: "#2D8659",
  blue: "#0A4D8C",
};

type SectionKey =
  | "geozones"
  | "open"
  | "specific"
  | "insurance"
  | "travelling"
  | "fpv";

const SECTION_ORDER: { key: SectionKey; title: string; icon: LucideIcon }[] = [
  { key: "geozones", title: "Geo-Zonen — Farblegende", icon: MapPin },
  { key: "open", title: "Offene Kategorie (Stand 1.1.2024)", icon: LayoutGrid },
  { key: "specific", title: "Spezielle Kategorie", icon: FileText },
  { key: "insurance", title: EASA_INSURANCE.title, icon: Shield },
  { key: "travelling", title: EASA_TRAVELLING.title, icon: Navigation },
  { key: "fpv", title: EASA_FPV.title, icon: Eye },
];

function LinkButton({ link }: { link?: { label: string; url: string } }) {
  if (!link) return null;
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1.5 self-start rounded-[10px] border border-[var(--color-app-border)] px-3 py-2 text-xs font-semibold text-brand-600 hover:border-brand-300 hover:bg-brand-50 transition"
      data-testid={`easa-link-${link.url}`}
    >
      <ExternalLink size={14} />
      <span>{link.label}</span>
    </a>
  );
}

function TopicSection({ section }: { section: EasaTopicSection }) {
  return (
    <div className="space-y-2">
      {section.paragraphs.map((p, i) => (
        <p key={i} className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
          {p}
        </p>
      ))}
      <LinkButton link={section.link} />
    </div>
  );
}

function OpenField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--color-app-text-tertiary)]">
        {label}
      </div>
      <div className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">{value}</div>
    </div>
  );
}

function GeoZones() {
  return (
    <div className="space-y-3">
      <p className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
        Mitgliedstaaten können geografische UAS-Gebiete (Geo-Zonen) festlegen. Die EASA
        verwendet zur Darstellung eine einheitliche Farbcodierung:
      </p>
      {EASA_GEO_ZONE_LEGEND.map((item) => (
        <div
          key={item.color}
          className="flex gap-3 rounded-xl border border-[var(--color-app-border)] p-3"
        >
          <span
            className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded-[5px]"
            style={{ backgroundColor: ZONE_COLORS[item.color] }}
          />
          <div className="flex-1 space-y-0.5">
            <div className="font-bold text-[var(--color-app-text)]">{item.title}</div>
            <div className="text-xs italic text-[var(--color-app-text-tertiary)]">{item.englishTerm}</div>
            <div className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
              {item.description}
            </div>
          </div>
        </div>
      ))}
      <p className="text-xs italic leading-relaxed text-[var(--color-app-text-tertiary)]">
        Hinweis: Es liegt in der Verantwortung des Fernpiloten, vor jedem Flug die aktuellen
        Geo-Zonen über die nationale Luftfahrtbehörde zu prüfen.
      </p>
      <LinkButton
        link={{
          label: "EASA — Geo-Zonen",
          url: "https://www.easa.europa.eu/light/topics/geo-zones-know-where-fly-your-drone",
        }}
      />
    </div>
  );
}

function OpenCategory() {
  return (
    <div className="space-y-3">
      <p className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
        Die offene Kategorie umfasst risikoarme Flüge ohne vorherige Genehmigung. Gemeinsame
        Regeln: max. 120 m über Grund, stets in Sichtweite (VLOS), nicht über
        Menschenansammlungen, Geo-Zonen beachten.
      </p>
      {EASA_OPEN_CATEGORY_TABLE.map((row, idx) => (
        <div key={idx} className="space-y-2 rounded-xl bg-[var(--color-app-bg-secondary)] p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 font-bold text-[var(--color-app-text)]">{row.classLabel}</div>
            <span className="rounded-xl bg-brand-100 px-2.5 py-1 text-xs font-bold text-brand-600">
              {row.subcategory}
            </span>
          </div>
          <OpenField label="Betriebsgrenzen" value={row.operationalLimits} />
          <OpenField label="Betreiber-Registrierung" value={row.operatorRegistration} />
          <OpenField label="Pilotenkompetenz" value={row.pilotCompetency} />
          <OpenField label="Mindestalter" value={row.minAge} />
        </div>
      ))}
      <LinkButton
        link={{
          label: "EASA — Offene Kategorie",
          url: "https://www.easa.europa.eu/domains/drones-air-mobility/operating-drone/open-category-low-risk-civil-drones",
        }}
      />
    </div>
  );
}

function SpecificCategory() {
  return (
    <div className="space-y-2">
      <p className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
        {EASA_SPECIFIC_CATEGORY.intro}
      </p>
      <h3 className="pt-1 font-bold text-[var(--color-app-text)]">Wann sie gilt</h3>
      {EASA_SPECIFIC_CATEGORY.whenApplies.map((w, i) => (
        <div key={i} className="flex items-start gap-1.5">
          <ChevronRight size={14} className="mt-0.5 shrink-0 text-brand-600" />
          <span className="flex-1 text-sm leading-relaxed text-[var(--color-app-text-secondary)]">{w}</span>
        </div>
      ))}
      <h3 className="pt-1 font-bold text-[var(--color-app-text)]">Die vier Wege zur Zulassung</h3>
      {EASA_SPECIFIC_CATEGORY.routes.map((route) => (
        <div key={route.key} className="space-y-1 rounded-xl bg-[var(--color-app-bg-secondary)] p-3">
          <div className="font-bold text-[var(--color-app-text)]">{route.title}</div>
          <div className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">{route.description}</div>
        </div>
      ))}
      <h3 className="pt-1 font-bold text-[var(--color-app-text)]">Betriebshandbuch</h3>
      <p className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
        {EASA_SPECIFIC_CATEGORY.operationsManual}
      </p>
      <h3 className="pt-1 font-bold text-[var(--color-app-text)]">Registrierung</h3>
      <p className="text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
        {EASA_SPECIFIC_CATEGORY.registration}
      </p>
      <LinkButton
        link={{
          label: "EASA — Spezielle Kategorie",
          url: "https://www.easa.europa.eu/domains/drones-air-mobility/operating-drone/specific-category-civil-drones",
        }}
      />
    </div>
  );
}

function SectionBody({ sectionKey }: { sectionKey: SectionKey }) {
  switch (sectionKey) {
    case "geozones":
      return <GeoZones />;
    case "open":
      return <OpenCategory />;
    case "specific":
      return <SpecificCategory />;
    case "insurance":
      return <TopicSection section={EASA_INSURANCE} />;
    case "travelling":
      return <TopicSection section={EASA_TRAVELLING} />;
    case "fpv":
      return <TopicSection section={EASA_FPV} />;
    default:
      return null;
  }
}

export default function EasaReference() {
  const [expanded, setExpanded] = useState<Set<SectionKey>>(
    new Set<SectionKey>(["geozones"]),
  );

  const toggle = (key: SectionKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-center text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
        Übersicht der EU-weiten Drohnenregeln nach EASA (EU 2019/947 &amp; 2019/945).
        Zusammenfassung der frei abrufbaren EASA-Inhalte — keine Rechtsberatung.
      </p>

      {SECTION_ORDER.map((section) => {
        const isExpanded = expanded.has(section.key);
        const Icon = section.icon;
        return (
          <div key={section.key} className="card overflow-hidden p-0">
            <button
              type="button"
              onClick={() => toggle(section.key)}
              className="flex w-full items-center gap-2 p-5 text-left transition hover:bg-[var(--color-app-bg-secondary)]"
              data-testid={`easa-section-${section.key}`}
            >
              <Icon size={20} className="shrink-0 text-brand-600" />
              <span className="flex-1 font-bold text-[var(--color-app-text)]">{section.title}</span>
              {isExpanded ? (
                <ChevronUp size={20} className="shrink-0 text-[var(--color-app-text-tertiary)]" />
              ) : (
                <ChevronDown size={20} className="shrink-0 text-[var(--color-app-text-tertiary)]" />
              )}
            </button>
            {isExpanded ? (
              <div className="px-5 pb-5">
                <SectionBody sectionKey={section.key} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
